import React, { useState, useEffect, useCallback } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { 
  ArrowLeft, Users, Plus, RefreshCw, Search, 
  Gamepad2, Crown, Lock, Globe, Wallet
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate or retrieve a persistent guest ID
const getGuestId = () => {
  let guestId = sessionStorage.getItem('metal_lego_guest_id');
  if (!guestId) {
    guestId = 'guest_' + Math.random().toString(36).substr(2, 8);
    sessionStorage.setItem('metal_lego_guest_id', guestId);
  }
  return guestId;
};

const LobbyBrowser = ({ onBack, onJoinLobby, onCreateLobby }) => {
  const { publicKey, connected } = useWallet();
  const { setVisible } = useWalletModal();
  const [lobbies, setLobbies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [createForm, setCreateForm] = useState({
    lobbyName: 'Metal Lego Co-op',
    playerName: 'Player',
    maxPlayers: 4
  });

  const walletAddress = publicKey?.toBase58() || getGuestId();

  // Fetch available lobbies
  const fetchLobbies = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/lobbies`);
      setLobbies(response.data.lobbies || []);
    } catch (error) {
      console.error('Error fetching lobbies:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchLobbies();
    const interval = setInterval(fetchLobbies, 10000);
    return () => clearInterval(interval);
  }, [fetchLobbies]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchLobbies();
  };

  // Create lobby
  const handleCreate = async () => {
    try {
      const response = await axios.post(`${API}/lobby/create`, {
        host_wallet: walletAddress,
        host_name: createForm.playerName || 'Player',
        max_players: createForm.maxPlayers,
        lobby_name: createForm.lobbyName
      });

      toast.success('Lobby created!');
      onCreateLobby(response.data.lobby.lobby_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create lobby');
    }
  };

  // Join lobby by code
  const handleJoinByCode = async () => {
    if (!joinCode.trim()) return;

    try {
      await axios.post(`${API}/lobby/${joinCode.toUpperCase()}/join`, {
        wallet_address: walletAddress,
        player_name: createForm.playerName || 'Player'
      });

      onJoinLobby(joinCode.toUpperCase());
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join lobby');
    }
  };

  // Join lobby from list
  const handleJoinLobby = async (lobbyId) => {
    try {
      await axios.post(`${API}/lobby/${lobbyId}/join`, {
        wallet_address: walletAddress,
        player_name: createForm.playerName || 'Player'
      });

      onJoinLobby(lobbyId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join lobby');
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            onClick={onBack}
            variant="ghost"
            className="text-[#A1A1AA] hover:text-white"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          
          {!connected ? (
            <Button
              onClick={() => setVisible(true)}
              className="text-sm flex items-center gap-2"
              style={{ 
                background: 'rgba(153, 69, 255, 0.2)',
                border: '1px solid #9945FF',
                color: '#9945FF'
              }}
              data-testid="lobby-wallet-btn"
            >
              <Wallet className="w-4 h-4" />
              Connect Wallet
            </Button>
          ) : (
            <span className="text-sm text-[#14F195] font-mono flex items-center gap-2">
              <Wallet className="w-4 h-4" />
              {publicKey.toBase58().slice(0, 4)}...{publicKey.toBase58().slice(-4)}
            </span>
          )}
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="font-game text-3xl md:text-4xl text-[#14F195] mb-2 flex items-center justify-center gap-3">
            <Users className="w-8 h-8" />
            MULTIPLAYER
          </h1>
          <p className="text-[#A1A1AA] text-sm">
            Create or join a co-op lobby to play with friends
          </p>
          {!connected && (
            <p className="text-[#FF9500] text-xs mt-2">
              Playing as Guest &mdash; connect wallet to save progress
            </p>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {/* Create Lobby */}
          <div className="glass-card p-6">
            <h2 className="font-game text-lg text-[#FFD700] mb-4 flex items-center gap-2">
              <Plus className="w-5 h-5" />
              CREATE LOBBY
            </h2>
            
            {!showCreate ? (
              <Button
                onClick={() => setShowCreate(true)}
                className="w-full bg-[#14F195] text-black hover:bg-[#14F195]/90 py-4"
                data-testid="show-create-btn"
              >
                <Gamepad2 className="w-5 h-5 mr-2" />
                CREATE NEW LOBBY
              </Button>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-[#A1A1AA] text-xs mb-1">Lobby Name</label>
                  <input
                    type="text"
                    value={createForm.lobbyName}
                    onChange={(e) => setCreateForm({ ...createForm, lobbyName: e.target.value })}
                    className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-[#14F195]"
                    placeholder="My Awesome Lobby"
                    data-testid="lobby-name-input"
                  />
                </div>
                
                <div>
                  <label className="block text-[#A1A1AA] text-xs mb-1">Your Name</label>
                  <input
                    type="text"
                    value={createForm.playerName}
                    onChange={(e) => setCreateForm({ ...createForm, playerName: e.target.value })}
                    className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-[#14F195]"
                    placeholder="Player"
                    data-testid="player-name-input"
                  />
                </div>
                
                <div>
                  <label className="block text-[#A1A1AA] text-xs mb-1">Max Players</label>
                  <div className="flex gap-2">
                    {[2, 3, 4].map(num => (
                      <Button
                        key={num}
                        onClick={() => setCreateForm({ ...createForm, maxPlayers: num })}
                        className={`flex-1 ${
                          createForm.maxPlayers === num 
                            ? 'bg-[#14F195] text-black' 
                            : 'bg-[#333]'
                        }`}
                      >
                        {num}
                      </Button>
                    ))}
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    onClick={() => setShowCreate(false)}
                    className="flex-1 bg-[#333] hover:bg-[#444]"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreate}
                    className="flex-1 bg-[#14F195] text-black hover:bg-[#14F195]/90"
                    data-testid="create-lobby-btn"
                  >
                    Create
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Join by Code */}
          <div className="glass-card p-6">
            <h2 className="font-game text-lg text-[#9945FF] mb-4 flex items-center gap-2">
              <Search className="w-5 h-5" />
              JOIN BY CODE
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-[#A1A1AA] text-xs mb-1">Lobby Code</label>
                <input
                  type="text"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-4 py-3 text-white text-center font-game text-xl tracking-widest focus:outline-none focus:border-[#9945FF] uppercase"
                  placeholder="ABCD1234"
                  maxLength={8}
                  data-testid="join-code-input"
                />
              </div>
              
              <Button
                onClick={handleJoinByCode}
                disabled={!joinCode.trim()}
                className="w-full bg-[#9945FF] hover:bg-[#9945FF]/90 py-4"
                data-testid="join-by-code-btn"
              >
                <Users className="w-5 h-5 mr-2" />
                JOIN LOBBY
              </Button>
            </div>
          </div>
        </div>

        {/* Available Lobbies */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-game text-lg text-white flex items-center gap-2">
              <Globe className="w-5 h-5 text-[#5AC8FA]" />
              AVAILABLE LOBBIES
            </h2>
            <Button
              onClick={handleRefresh}
              disabled={refreshing}
              className="bg-[#333] hover:bg-[#444]"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-[#14F195] border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-[#A1A1AA]">Loading lobbies...</p>
            </div>
          ) : lobbies.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-[#333] mx-auto mb-4" />
              <p className="text-[#A1A1AA]">No lobbies available</p>
              <p className="text-[#666] text-sm mt-2">Create one to get started!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {lobbies.map((lobby) => (
                <div
                  key={lobby.lobby_id}
                  className="flex items-center justify-between p-4 bg-[#1a1a1a] rounded-lg border border-[#333] hover:border-[#14F195] transition-colors"
                  data-testid={`lobby-item-${lobby.lobby_id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#14F195]/20 rounded-lg flex items-center justify-center">
                      <Gamepad2 className="w-6 h-6 text-[#14F195]" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-game text-white">{lobby.lobby_name}</span>
                        <span className="text-xs text-[#666] bg-[#333] px-2 py-0.5 rounded">
                          {lobby.lobby_id}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-[#A1A1AA]">
                        <span className="flex items-center gap-1">
                          <Crown className="w-3 h-3 text-[#FFD700]" />
                          {lobby.players?.[0]?.name || 'Host'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {lobby.players?.length || 0}/{lobby.max_players}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <Button
                    onClick={() => handleJoinLobby(lobby.lobby_id)}
                    disabled={lobby.players?.length >= lobby.max_players}
                    className={`${
                      lobby.players?.length >= lobby.max_players
                        ? 'bg-[#333] text-[#666] cursor-not-allowed'
                        : 'bg-[#14F195] text-black hover:bg-[#14F195]/90'
                    }`}
                    data-testid={`join-lobby-${lobby.lobby_id}`}
                  >
                    {lobby.players?.length >= lobby.max_players ? (
                      <>
                        <Lock className="w-4 h-4 mr-2" />
                        FULL
                      </>
                    ) : (
                      'JOIN'
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="mt-6 text-center">
          <p className="text-[#666] text-sm">
            Co-op mode: Team up with friends to fight through waves of enemies together!
          </p>
        </div>
      </div>
    </div>
  );
};

export default LobbyBrowser;
